/**
 * Checklists Hooks (Phase 9.6)
 *
 * TanStack Query hooks for checklist CRUD operations,
 * checklist completions, and compliance analytics.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useApi } from '../lib/hooks/useApi';
import type {
  ChecklistWithStation,
  ChecklistWithItems,
  ChecklistLookup,
  Checklist,
  CreateChecklist,
  UpdateChecklist,
  ChecklistItem,
  CreateChecklistItem,
  UpdateChecklistItem,
  ReorderChecklistItems,
  ChecklistCompletionWithDetails,
  CreateChecklistCompletion,
  ComplianceAnalyticsItem,
  ComplianceSummary,
  ChecklistFrequency,
  ChecklistCompletionType,
  ThresholdStatus,
  ComplianceGroupBy,
} from '@guild/shared';

// ============================================
// QUERY KEYS
// ============================================

export const checklistKeys = {
  all: ['checklists'] as const,
  lists: () => [...checklistKeys.all, 'list'] as const,
  list: (filters: { search?: string; stationId?: string; frequency?: string; includeInactive?: boolean }) =>
    [...checklistKeys.lists(), filters] as const,
  lookup: (stationId?: string) => [...checklistKeys.all, 'lookup', stationId] as const,
  details: () => [...checklistKeys.all, 'detail'] as const,
  detail: (id: string) => [...checklistKeys.details(), id] as const,
};

export const checklistCompletionKeys = {
  all: ['checklist-completions'] as const,
  lists: () => [...checklistCompletionKeys.all, 'list'] as const,
  list: (filters: Record<string, unknown>) => [...checklistCompletionKeys.lists(), filters] as const,
  details: () => [...checklistCompletionKeys.all, 'detail'] as const,
  detail: (id: string) => [...checklistCompletionKeys.details(), id] as const,
};

export const checklistAnalyticsKeys = {
  all: ['checklist-analytics'] as const,
  compliance: (filters: Record<string, unknown>) => [...checklistAnalyticsKeys.all, 'compliance', filters] as const,
};

// ============================================
// CHECKLIST HOOKS
// ============================================

/**
 * Fetch checklists list with station name and item count
 */
export function useChecklists(options?: {
  search?: string;
  stationId?: string;
  frequency?: ChecklistFrequency;
  includeInactive?: boolean;
}) {
  const api = useApi();

  return useQuery({
    queryKey: checklistKeys.list(options ?? {}),
    queryFn: async () => {
      const params = new URLSearchParams();
      if (options?.search) params.set('search', options.search);
      if (options?.stationId) params.set('stationId', options.stationId);
      if (options?.frequency) params.set('frequency', options.frequency);
      if (options?.includeInactive) params.set('includeInactive', 'true');

      const queryString = params.toString();
      const endpoint = queryString ? `/api/checklists?${queryString}` : '/api/checklists';

      return api.get<ChecklistWithStation[]>(endpoint);
    },
  });
}

/**
 * Fetch lightweight checklists list for dropdowns
 */
export function useChecklistsLookup(stationId?: string) {
  const api = useApi();

  return useQuery({
    queryKey: checklistKeys.lookup(stationId),
    queryFn: async () => {
      const params = new URLSearchParams();
      if (stationId) params.set('stationId', stationId);

      const queryString = params.toString();
      const endpoint = queryString ? `/api/checklists/lookup?${queryString}` : '/api/checklists/lookup';

      return api.get<ChecklistLookup[]>(endpoint);
    },
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });
}

/**
 * Fetch single checklist with items
 */
export function useChecklist(id: string | null) {
  const api = useApi();

  return useQuery({
    queryKey: checklistKeys.detail(id ?? ''),
    queryFn: () => api.get<ChecklistWithItems>(`/api/checklists/${id}`),
    enabled: !!id,
  });
}

/**
 * Create checklist with items mutation
 */
export function useCreateChecklist() {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateChecklist) => api.post<ChecklistWithItems>('/api/checklists', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: checklistKeys.all });
    },
  });
}

/**
 * Update checklist mutation
 */
export function useUpdateChecklist() {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateChecklist }) =>
      api.put<Checklist>(`/api/checklists/${id}`, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: checklistKeys.lists() });
      queryClient.invalidateQueries({ queryKey: checklistKeys.detail(variables.id) });
      queryClient.invalidateQueries({ queryKey: checklistKeys.lookup() });
    },
  });
}

/**
 * Delete checklist mutation (soft delete)
 */
export function useDeleteChecklist() {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => api.delete<{ id: string }>(`/api/checklists/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: checklistKeys.all });
    },
  });
}

// ============================================
// CHECKLIST ITEM HOOKS
// ============================================

/**
 * Add item to checklist mutation
 */
export function useAddChecklistItem() {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ checklistId, data }: { checklistId: string; data: CreateChecklistItem }) =>
      api.post<ChecklistItem>(`/api/checklists/${checklistId}/items`, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: checklistKeys.detail(variables.checklistId) });
      queryClient.invalidateQueries({ queryKey: checklistKeys.lists() });
    },
  });
}

/**
 * Update checklist item mutation
 */
export function useUpdateChecklistItem() {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      checklistId,
      itemId,
      data,
    }: {
      checklistId: string;
      itemId: string;
      data: UpdateChecklistItem;
    }) => api.put<ChecklistItem>(`/api/checklists/${checklistId}/items/${itemId}`, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: checklistKeys.detail(variables.checklistId) });
    },
  });
}

/**
 * Delete checklist item mutation (soft delete)
 */
export function useDeleteChecklistItem() {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ checklistId, itemId }: { checklistId: string; itemId: string }) =>
      api.delete<{ id: string }>(`/api/checklists/${checklistId}/items/${itemId}`),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: checklistKeys.detail(variables.checklistId) });
      queryClient.invalidateQueries({ queryKey: checklistKeys.lists() });
    },
  });
}

/**
 * Reorder checklist items mutation
 */
export function useReorderChecklistItems() {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ checklistId, data }: { checklistId: string; data: ReorderChecklistItems }) =>
      api.put<ChecklistItem[]>(`/api/checklists/${checklistId}/items/reorder`, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: checklistKeys.detail(variables.checklistId) });
    },
  });
}

// ============================================
// CHECKLIST COMPLETION HOOKS
// ============================================

interface CompletionListFilters {
  checklistId?: string;
  stationId?: string;
  shiftId?: string;
  completionType?: ChecklistCompletionType;
  thresholdStatus?: ThresholdStatus;
  startDate?: Date;
  endDate?: Date;
  limit?: number;
  offset?: number;
}

/**
 * Fetch checklist completions list
 */
export function useChecklistCompletions(options?: CompletionListFilters) {
  const api = useApi();

  return useQuery({
    queryKey: checklistCompletionKeys.list((options ?? {}) as Record<string, unknown>),
    queryFn: async () => {
      const params = new URLSearchParams();
      if (options?.checklistId) params.set('checklistId', options.checklistId);
      if (options?.stationId) params.set('stationId', options.stationId);
      if (options?.shiftId) params.set('shiftId', options.shiftId);
      if (options?.completionType) params.set('completionType', options.completionType);
      if (options?.thresholdStatus) params.set('thresholdStatus', options.thresholdStatus);
      if (options?.startDate) params.set('startDate', options.startDate.toISOString());
      if (options?.endDate) params.set('endDate', options.endDate.toISOString());
      if (options?.limit) params.set('limit', options.limit.toString());
      if (options?.offset) params.set('offset', options.offset.toString());

      const queryString = params.toString();
      const endpoint = queryString
        ? `/api/checklist-completions?${queryString}`
        : '/api/checklist-completions';

      return api.get<ChecklistCompletionWithDetails[]>(endpoint);
    },
  });
}

/**
 * Fetch single checklist completion
 */
export function useChecklistCompletion(id: string | null) {
  const api = useApi();

  return useQuery({
    queryKey: checklistCompletionKeys.detail(id ?? ''),
    queryFn: () => api.get<ChecklistCompletionWithDetails>(`/api/checklist-completions/${id}`),
    enabled: !!id,
  });
}

/**
 * Create manual checklist completion mutation
 */
export function useCreateChecklistCompletion() {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateChecklistCompletion) =>
      api.post<ChecklistCompletionWithDetails>('/api/checklist-completions', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: checklistCompletionKeys.all });
      queryClient.invalidateQueries({ queryKey: checklistAnalyticsKeys.all });
    },
  });
}

// ============================================
// COMPLIANCE ANALYTICS HOOKS
// ============================================

interface ComplianceAnalyticsFilters {
  groupBy?: ComplianceGroupBy;
  checklistId?: string;
  stationId?: string;
  shiftId?: string;
  startDate?: Date;
  endDate?: Date;
  period?: number;
}

interface ComplianceAnalyticsResponse {
  groupBy: ComplianceGroupBy;
  groups: ComplianceAnalyticsItem[];
  summary: ComplianceSummary;
  filters: {
    startDate: string;
    endDate: string;
    checklistId?: string;
    stationId?: string;
    shiftId?: string;
  };
}

/**
 * Fetch checklist compliance analytics
 */
export function useChecklistComplianceAnalytics(options?: ComplianceAnalyticsFilters) {
  const api = useApi();

  return useQuery({
    queryKey: checklistAnalyticsKeys.compliance((options ?? {}) as Record<string, unknown>),
    queryFn: async () => {
      const params = new URLSearchParams();
      if (options?.groupBy) params.set('groupBy', options.groupBy);
      if (options?.checklistId) params.set('checklistId', options.checklistId);
      if (options?.stationId) params.set('stationId', options.stationId);
      if (options?.shiftId) params.set('shiftId', options.shiftId);
      if (options?.startDate) params.set('startDate', options.startDate.toISOString());
      if (options?.endDate) params.set('endDate', options.endDate.toISOString());
      if (options?.period) params.set('period', options.period.toString());

      const queryString = params.toString();
      const endpoint = queryString
        ? `/api/analytics/checklist-compliance?${queryString}`
        : '/api/analytics/checklist-compliance';

      return api.get<ComplianceAnalyticsResponse>(endpoint);
    },
  });
}

/**
 * Get compliance summary (convenience wrapper)
 */
export function useComplianceSummary(options?: {
  checklistId?: string;
  stationId?: string;
  period?: number;
}) {
  const query = useChecklistComplianceAnalytics({
    groupBy: 'checklist',
    ...options,
  });

  return {
    ...query,
    data: query.data?.summary,
  };
}
